import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertCircle, ExternalLink, Hotel, Percent, Search } from 'lucide-react'
import { getContentList } from '../../lib/api/content'
import { useToggleEnabled } from './useToggleEnabled'
import { Stat, StateChip, Switch } from './ui'
import { cn } from '../../lib/cn'

type El = Record<string, unknown>
const isYes = (v: unknown) => v === true || v === 'true' || v === 1 || v === '1'

const LIST_KEY = ['content', 'convention', 'all'] as const

const SEG = [
  { value: 'all', label: 'Tutti' },
  { value: 'active', label: 'Attivi' },
  { value: 'inactive', label: 'Disattivi' },
] as const

function stripHtml(v: unknown): string {
  return String(v ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

type Hotel = { id: string; name: string; description: string; enabled: boolean }

export function ConventionsPage() {
  const [seg, setSeg] = useState<(typeof SEG)[number]['value']>('all')
  const [q, setQ] = useState('')
  const [busyId, setBusyId] = useState<string | null>(null)

  const list = useQuery({
    queryKey: LIST_KEY,
    queryFn: () => getContentList('convention', { limit: 500 }),
  })
  const toggle = useToggleEnabled('convention', LIST_KEY)

  const hotels = useMemo<Hotel[]>(() => {
    const rows = list.data?.data ?? []
    return rows
      .map((r) => {
        const el = r.elements as El
        return {
          id: r.id,
          name: String(el.name ?? el.label ?? 'Senza nome'),
          description: stripHtml(el.description),
          enabled: isYes(el.enabled),
        }
      })
      .sort((a, b) => a.name.localeCompare(b.name, 'it'))
  }, [list.data])

  const stats = useMemo(() => {
    const active = hotels.filter((h) => h.enabled).length
    return { total: hotels.length, active, inactive: hotels.length - active }
  }, [hotels])

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return hotels.filter((h) => {
      if (seg === 'active' && !h.enabled) return false
      if (seg === 'inactive' && h.enabled) return false
      if (needle && !h.name.toLowerCase().includes(needle)) return false
      return true
    })
  }, [hotels, seg, q])

  function flip(h: Hotel) {
    setBusyId(h.id)
    toggle.mutate({ id: h.id, enabled: !h.enabled }, { onSettled: () => setBusyId(null) })
  }

  return (
    <div className="w-full px-6 pb-16 pt-7 lg:px-9">
      {/* page-head */}
      <div className="mb-6 flex flex-wrap items-end gap-4">
        <div>
          <h2 className="font-display text-[26px] font-bold leading-tight tracking-[-.3px] text-coal sm:text-[28px]">
            Hotel convenzionati
          </h2>
          <p className="mt-1.5 max-w-[70ch] text-[13.5px] text-muted">
            Strutture partner con tariffa dedicata per i loro ospiti. Attiva o disattiva una convenzione direttamente da
            qui; la scontistica si imposta in <span className="text-coal">Convenzioni → Scontistica</span>.
          </p>
        </div>
        <a
          href="/admin/yep-admin/content/convention/list"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 rounded-lg border border-line bg-card px-4 py-[10px] text-[13.5px] font-semibold text-slate hover:border-faint sm:ml-auto"
        >
          <ExternalLink className="h-4 w-4" /> Gestionale
        </a>
      </div>

      {/* stat cards */}
      <div className="mb-[18px] grid grid-cols-3 gap-[14px]">
        <Stat label="Convenzioni" value={String(stats.total)} sub="strutture totali" />
        <Stat label="Attive" value={String(stats.active)} sub="visibili in prenotazione" />
        <Stat label="Disattive" value={String(stats.inactive)} sub="sospese" />
      </div>

      {/* toolbar: filter + search */}
      <div className="mb-[18px] flex flex-wrap items-center gap-2.5">
        <div className="inline-flex overflow-hidden rounded-lg border border-line bg-card">
          {SEG.map((s) => (
            <button
              key={s.value}
              onClick={() => setSeg(s.value)}
              className={cn(
                'px-3.5 py-2 text-[13px] font-semibold transition-colors',
                seg === s.value ? 'bg-lagoon text-white' : 'text-muted hover:text-coal',
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="relative ml-auto w-full sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-faint" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca hotel…"
            className="h-10 w-full rounded-lg border border-line bg-card pl-9 pr-3 text-[13.5px] text-coal outline-none placeholder:text-faint focus:border-verdigris"
          />
        </div>
      </div>

      {/* hotel grid */}
      {list.isError ? (
        <div className="cv-panel px-4 py-16 text-center text-sm text-muted">
          <AlertCircle className="mx-auto mb-2 h-5 w-5 text-venetian" /> Impossibile caricare le convenzioni.
        </div>
      ) : list.isLoading ? (
        <div className="grid grid-cols-1 gap-[14px] sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="cv-panel p-4">
              <div className="skeleton h-24 w-full rounded bg-line-soft" />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="cv-panel px-4 py-16 text-center text-sm text-muted">
          {hotels.length === 0 ? 'Nessuna convenzione presente.' : 'Nessun hotel corrisponde al filtro.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 items-start gap-[14px] sm:grid-cols-2 xl:grid-cols-3">
          {filtered.map((h) => (
            <div
              key={h.id}
              className={cn(
                'cv-panel flex flex-col overflow-hidden transition-opacity',
                !h.enabled && 'opacity-75',
              )}
            >
              <div className="flex items-start gap-3 p-4">
                <span
                  className={cn(
                    'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl font-display text-[18px] font-bold ring-1',
                    h.enabled ? 'bg-verdigris/10 text-verdigris ring-verdigris/20' : 'bg-paper text-faint ring-line',
                  )}
                >
                  {h.name.charAt(0).toUpperCase() || <Hotel className="h-5 w-5" />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start gap-2">
                    <h3 className="min-w-0 flex-1 truncate font-display text-[15.5px] font-bold leading-snug text-coal" title={h.name}>
                      {h.name}
                    </h3>
                    <StateChip on={h.enabled} />
                  </div>
                  <p className="mt-1 line-clamp-2 text-[12.5px] leading-snug text-muted">
                    {h.description || <span className="text-faint">Nessuna descrizione</span>}
                  </p>
                </div>
              </div>
              <div className="mt-auto flex items-center justify-between border-t border-line-soft px-4 py-2.5">
                <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-muted">
                  <Percent className="h-3.5 w-3.5 text-faint" /> Sconto in Scontistica
                </span>
                <label className="flex items-center gap-2 text-[12px] font-semibold text-slate">
                  <span className={h.enabled ? 'text-coal' : 'text-muted'}>{h.enabled ? 'Attivo' : 'Disattivo'}</span>
                  <Switch on={h.enabled} busy={busyId === h.id} onClick={() => flip(h)} label={`Attiva ${h.name}`} />
                </label>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
